<template>
  <v-container fluid class="pa-0">
    <v-card class="mb-4 pa-4" outlined>
      <v-card-title class="pa-0 text-h6">Задачи на обзвон</v-card-title>
      <v-divider class="my-3" />
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="search"
            label="Поиск..."
            filled
            dense
            hide-details
            @keyup.enter="updateFilter"
          />
        </v-col>
        <v-col cols="12" class="d-flex align-center">
          <v-btn color="primary" small @click="updateFilter">Поиск</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined>
      <v-card-text>
        <div class="d-flex justify-center my-4">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
          />
        </div>

        <v-data-table
          :headers="headers"
          :items="paginatedRows"
          :loading="loading"
          loading-text="Загрузка…"
          class="elevation-0"
          dense
          hide-default-footer
          :items-per-page="rowsPerPage"
        >
          <template #item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>
              {{ item.status }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <div class="call-tasks-actions-wrap d-flex justify-center">
              <div class="d-flex flex-nowrap align-center" style="gap: 2px">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      x-small
                      color="success"
                      icon
                      v-on="on"
                      @click="onStart(item)"
                    >
                      <v-icon small>mdi-play</v-icon>
                    </v-btn>
                  </template>
                  <span>Запустить</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      x-small
                      color="warning"
                      icon
                      v-on="on"
                      @click="onPause(item)"
                    >
                      <v-icon small>mdi-pause</v-icon>
                    </v-btn>
                  </template>
                  <span>Пауза</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      x-small
                      color="error"
                      icon
                      v-on="on"
                      @click="onStop(item)"
                    >
                      <v-icon small>mdi-stop</v-icon>
                    </v-btn>
                  </template>
                  <span>Остановить</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      x-small
                      color="info"
                      icon
                      v-on="on"
                      @click="onEdit(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Редактировать</span>
                </v-tooltip>
              </div>
            </div>
          </template>
        </v-data-table>

        <div class="d-flex justify-center my-4">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
          />
        </div>
      </v-card-text>
    </v-card>

    <div class="call-tasks-fab">
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            color="success"
            dark
            fab
            v-on="on"
            @click="onCreate"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Создать задачу</span>
      </v-tooltip>
    </div>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'CallTasksList',
  data() {
    return {
      tasks: [],
      loading: false,
      search: '',
      page: 1,
      rowsPerPage: 20,
      headers: [
        { text: 'ID', value: 'id', align: 'right' },
        { text: 'Название', value: 'name' },
        { text: 'ID проекта', value: 'project_id', align: 'center' },
        { text: 'Статус', value: 'status', align: 'center', sortable: false },
        { text: 'Действия', value: 'actions', align: 'center', sortable: false },
      ],
    }
  },
  computed: {
    filteredTasks() {
      if (!this.search) return this.tasks
      const term = this.search.toLowerCase()
      return this.tasks.filter((task) => {
        const name = (task.name && String(task.name).toLowerCase()) || ''
        return (
          name.includes(term) ||
          String(task.id).includes(term) ||
          String(task.project_id).includes(term)
        )
      })
    },
    totalPages() {
      if (this.filteredTasks.length === 0) return 1
      return Math.max(1, Math.ceil(this.filteredTasks.length / this.rowsPerPage))
    },
    paginatedRows() {
      const start = (this.page - 1) * this.rowsPerPage
      return this.filteredTasks.slice(start, start + this.rowsPerPage)
    },
  },
  watch: {
    search() {
      this.page = 1
    },
  },
  mounted() {
    this.loadTasks()
  },
  methods: {
    ...mapActions('call_tasks', [
      'fetchTasks',
      'startTask',
      'stopTask',
      'pauseTask',
    ]),
    notifyError(message) {
      this.$store.commit('shared/SET_ERROR', { message }, { root: true })
    },
    notifySuccess(message) {
      this.$store.commit('shared/SET_SUCCESS', { message }, { root: true })
    },
    async loadTasks() {
      this.loading = true
      try {
        const data = await this.fetchTasks()
        this.tasks = Array.isArray(data) ? data : data.data || []
        this.page = 1
      } catch (err) {
        this.notifyError('Ошибка загрузки')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    updateFilter() {
      this.page = 1
    },
    async onStart(row) {
      try {
        await this.startTask(row.id)
        row.status = 'running'
        this.notifySuccess('Задача запущена')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка запуска задачи'
        this.notifyError(message)
      }
    },
    async onPause(row) {
      try {
        await this.pauseTask(row.id)
        row.status = 'paused'
        this.notifySuccess('Задача поставлена на паузу')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка паузы задачи'
        this.notifyError(message)
      }
    },
    async onStop(row) {
      try {
        await this.stopTask(row.id)
        row.status = 'stopped'
        this.notifySuccess('Задача остановлена')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка остановки задачи'
        this.notifyError(message)
      }
    },
    onEdit(row) {
      this.$router.push({
        name: 'call-tasks.edit',
        params: { id: String(row.id) },
      })
    },
    onCreate() {
      this.$router.push({ name: 'call-tasks.create' })
    },
    statusColor(status) {
      switch (status) {
        case 'running':
          return 'success'
        case 'paused':
          return 'warning'
        case 'stopped':
          return 'error'
        default:
          return 'grey'
      }
    },
  },
}
</script>

<style scoped>
.call-tasks-fab {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 4;
}
/* Группа кнопок — по центру ячейки, на всю ширину td */
.call-tasks-actions-wrap {
  width: 100%;
}
</style>
